export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  salary_range?: {
    min: number;
    max: number;
    currency: string;
  };
  posted_at: string;
  expires_at?: string;
  hard_expire_at?: string;
  last_refreshed_at?: string;
  paused_at?: string;
  expired_at?: string;
  closed_at?: string;
  status: JobStatus;
  display_status?: JobStatus;
  source_status?: JobStatus;
  close_reason?: JobCloseReason;
  ats_protected?: boolean;
  apply_cap?: number;
  company_logo?: string;
  company_description?: string;
  apply_url?: string;
  skills?: string[];
  benefits?: string[];
}

// Community-level expiration settings
export interface CommunityExpirationSettings {
  id: string;
  community_id: string;
  default_expiration_days: number;
  hard_expire_offset_days: number;
  evergreen_refresh_days?: number;
  auto_pause_threshold?: number;
  notification_channels: ('email' | 'slack')[];
  warning_days: number[];
  ats_sync_expiration: boolean;
  created_at: string;
  updated_at: string;
}

// Job lifecycle events for audit/outbox
export interface JobLifecycleEvent {
  id: string;
  job_id: string;
  community_id: string;
  event_type: 'created' | 'published' | 'paused' | 'resumed' | 'expired_soft' | 'expired_hard' | 'extended' | 'closed' | 'archived';
  old_status?: JobStatus;
  new_status: JobStatus;
  triggered_by: 'manual' | 'system_expiration' | 'ats_sync' | 'application_cap';
  metadata: Record<string, any>;
  processed_at?: string;
  created_at: string;
  ats_sync_required: boolean;
  ats_sync_completed: boolean;
  ats_sync_error?: string;
}

// Job expiration utilities
export interface JobExpirationInfo {
  expires_at?: string;
  hard_expire_at?: string;
  days_until_expiry?: number;
  days_until_hard_expiry?: number;
  is_expiring_soon: boolean; // Within warning threshold
  is_expired: boolean;       // Past soft expiration
  is_hard_expired: boolean;  // Past hard expiration
  can_extend: boolean;       // Status allows extension
}

// Extension request payload
export interface ExtendJobRequest {
  job_id: string;
  extension_days: number;
}

// Extension response
export interface ExtendJobResponse {
  success: boolean;
  job_id: string;
  old_expires_at?: string;
  new_expires_at?: string;
  new_hard_expire_at?: string;
  extension_days?: number;
  status_changed?: boolean;
  error?: string;
}

// Job Status Types and Utilities (Updated for Expiration System)
export type JobStatus =
  | 'open'          // Legacy - maps to 'published'
  | 'published'     // Actively recruiting
  | 'paused'        // Temporarily paused (replaces 'on_hold')
  | 'expired_soft'  // Expired but can be extended
  | 'closed'        // Position filled/closed
  | 'on_hold'       // Legacy - maps to 'paused'
  | 'cancelled'     // Position cancelled
  | 'archived'      // Historical reference
  | 'draft';        // Unpublished

export type JobCloseReason =
  | 'filled'
  | 'time_expired'
  | 'on_hold'
  | 'capped_by_apps'
  | 'manual_close'
  | 'ats_sync_closed'
  | 'cancelled';

export const JOB_STATUSES = {
  // Current system statuses
  PUBLISHED: 'published' as const,
  PAUSED: 'paused' as const,
  EXPIRED_SOFT: 'expired_soft' as const,
  CLOSED: 'closed' as const,
  ARCHIVED: 'archived' as const,
  DRAFT: 'draft' as const,
  // Legacy statuses (for backwards compatibility)
  OPEN: 'open' as const,        // Maps to PUBLISHED
  ON_HOLD: 'on_hold' as const,  // Maps to PAUSED
  CANCELLED: 'cancelled' as const,
} as const;

export const JOB_CLOSE_REASONS = {
  FILLED: 'filled' as const,
  TIME_EXPIRED: 'time_expired' as const,
  ON_HOLD: 'on_hold' as const,
  CAPPED_BY_APPS: 'capped_by_apps' as const,
  MANUAL_CLOSE: 'manual_close' as const,
  ATS_SYNC_CLOSED: 'ats_sync_closed' as const,
  CANCELLED: 'cancelled' as const,
} as const;

export const JOB_STATUS_LABELS = {
  [JOB_STATUSES.PUBLISHED]: 'Published',
  [JOB_STATUSES.PAUSED]: 'Paused',
  [JOB_STATUSES.EXPIRED_SOFT]: 'Expired',
  [JOB_STATUSES.CLOSED]: 'Closed',
  [JOB_STATUSES.ARCHIVED]: 'Archived',
  [JOB_STATUSES.DRAFT]: 'Draft',
  // Legacy mappings
  [JOB_STATUSES.OPEN]: 'Open',
  [JOB_STATUSES.ON_HOLD]: 'On Hold',
  [JOB_STATUSES.CANCELLED]: 'Cancelled',
} as const;

export const JOB_STATUS_DESCRIPTIONS = {
  [JOB_STATUSES.PUBLISHED]: 'Actively recruiting candidates',
  [JOB_STATUSES.PAUSED]: 'Recruitment temporarily paused',
  [JOB_STATUSES.EXPIRED_SOFT]: 'Job expired but can be extended',
  [JOB_STATUSES.CLOSED]: 'Position has been filled or closed',
  [JOB_STATUSES.ARCHIVED]: 'Historical job for reference',
  [JOB_STATUSES.DRAFT]: 'Unpublished job posting',
  // Legacy mappings
  [JOB_STATUSES.OPEN]: 'Actively recruiting candidates',
  [JOB_STATUSES.ON_HOLD]: 'Recruitment temporarily paused',
  [JOB_STATUSES.CANCELLED]: 'Position cancelled or removed',
} as const;

export const JOB_STATUS_ICONS = {
  [JOB_STATUSES.PUBLISHED]: 'CheckCircle',
  [JOB_STATUSES.PAUSED]: 'Pause',
  [JOB_STATUSES.EXPIRED_SOFT]: 'Clock',
  [JOB_STATUSES.CLOSED]: 'XCircle',
  [JOB_STATUSES.ARCHIVED]: 'Archive',
  [JOB_STATUSES.DRAFT]: 'FileText',
  // Legacy mappings
  [JOB_STATUSES.OPEN]: 'CheckCircle',
  [JOB_STATUSES.ON_HOLD]: 'Pause',
  [JOB_STATUSES.CANCELLED]: 'X',
} as const;

export const JOB_CLOSE_REASON_LABELS = {
  [JOB_CLOSE_REASONS.FILLED]: 'Position Filled',
  [JOB_CLOSE_REASONS.TIME_EXPIRED]: 'Time Expired',
  [JOB_CLOSE_REASONS.ON_HOLD]: 'On Hold',
  [JOB_CLOSE_REASONS.CAPPED_BY_APPS]: 'Application Limit Reached',
  [JOB_CLOSE_REASONS.MANUAL_CLOSE]: 'Manually Closed',
  [JOB_CLOSE_REASONS.ATS_SYNC_CLOSED]: 'Closed via ATS',
  [JOB_CLOSE_REASONS.CANCELLED]: 'Cancelled',
} as const;

// Active statuses (visible to job seekers)
export const ACTIVE_JOB_STATUSES: JobStatus[] = [JOB_STATUSES.PUBLISHED, JOB_STATUSES.OPEN];

// Legacy alias for backwards compatibility
export const ACTIVE_STATUSES = ACTIVE_JOB_STATUSES;

// Statuses that can be extended
export const EXTENDABLE_JOB_STATUSES: JobStatus[] = [
  JOB_STATUSES.PUBLISHED, 
  JOB_STATUSES.PAUSED, 
  JOB_STATUSES.EXPIRED_SOFT,
  JOB_STATUSES.OPEN,      // Legacy support
  JOB_STATUSES.ON_HOLD    // Legacy support
];

// Inactive statuses (not visible to job seekers)
export const INACTIVE_JOB_STATUSES: JobStatus[] = [
  JOB_STATUSES.CLOSED,
  JOB_STATUSES.PAUSED,
  JOB_STATUSES.EXPIRED_SOFT,
  JOB_STATUSES.ARCHIVED,
  JOB_STATUSES.DRAFT,
  // Legacy
  JOB_STATUSES.ON_HOLD,
  JOB_STATUSES.CANCELLED,
];

// Status mapping for legacy compatibility
export const LEGACY_STATUS_MAPPING = {
  [JOB_STATUSES.OPEN]: JOB_STATUSES.PUBLISHED,
  [JOB_STATUSES.ON_HOLD]: JOB_STATUSES.PAUSED,
} as const;

export interface JobApplication {
  id: string;
  job_id: string;
  user_id: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  submitted_at: string;
  resume_url?: string;
  cover_letter?: string;
}

export interface JobFilters {
  type?: string[];
  location?: string[];
  skills?: string[];
  salary_range?: {
    min: number;
    max: number;
  };
  // Enhanced filters for Phase 2
  employment_type?: string[];
  remote_type?: string[];
  experience_level?: string[];
  company?: string[];
  date_posted?: 'last_7_days' | 'last_30_days' | 'last_90_days' | 'all_time';
  featured_only?: boolean;
  search_term?: string;
}

// Filter options constants
export const EMPLOYMENT_TYPES = [
  'full-time',
  'part-time',
  'contract',
  'internship',
  'temporary',
  'freelance',
] as const;

export const REMOTE_TYPES = ['remote', 'hybrid', 'on_site'] as const;

export const EXPERIENCE_LEVELS = [
  'entry',
  'mid',
  'senior',
  'lead',
  'executive',
] as const;

export const DATE_POSTED_OPTIONS = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'all_time', label: 'All time' },
] as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];
export type RemoteType = (typeof REMOTE_TYPES)[number];
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];
export type DatePostedFilter = (typeof DATE_POSTED_OPTIONS)[number]['value'];
